import { useState, useRef, useEffect } from "react";
import {
  Send,
  Image,
  Paperclip,
  MoreVertical,
  RefreshCw,
  Phone,
  Check,
  CheckCheck,
  Clock,
  Zap,
  X,
  UserPlus,
  AlertTriangle,
  Timer,
  Bot,
  Reply,
  Download,
  Trash2,
  ChevronDown,
  ScanText,
  ClipboardCopy,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { format } from "date-fns";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusOptions = [
  { value: "pending", label: "Pending", color: "bg-amber-500" },
  { value: "done", label: "Done", color: "bg-emerald-500" },
  { value: "enquiry", label: "Enquiry", color: "bg-blue-500" },
];


// QuotedReply Component for displaying replied message context
const QuotedReply = ({ contextMessageId, messages, isOutgoing }) => {
  if (!contextMessageId || !messages || messages.length === 0) return null;
  
  const quotedMsg = messages.find(m => m && m.whatsapp_message_id === contextMessageId);

  const scrollToMessage = () => {
    const el = document.getElementById(`msg-${contextMessageId}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.style.backgroundColor = 'rgba(59, 130, 246, 0.2)';
      setTimeout(() => { el.style.backgroundColor = ''; }, 2000);
    }
  };

  return (
    <div 
      onClick={scrollToMessage}
      className={`mb-2 p-2 rounded cursor-pointer hover:opacity-80 transition-opacity border-l-4 ${
        isOutgoing 
          ? 'bg-white/20 border-white/60' 
          : 'bg-gray-100 border-gray-400'
      }`}
    >
      <p className={`text-xs font-medium mb-0.5 ${isOutgoing ? 'text-white/70' : 'text-gray-500'}`}>
        {quotedMsg ? (quotedMsg.direction === 'incoming' ? 'Customer' : 'You') : 'Quoted message'}
      </p>
      <p className={`text-sm line-clamp-2 ${isOutgoing ? 'text-white/90' : 'text-gray-700'}`}>
        {quotedMsg ? (quotedMsg.content || (quotedMsg.media_local_path ? '[Image]' : '[Message]')) : '[Original message not available]'}
      </p>
    </div>
  );
};

const WHATSAPP_ACCOUNT_MAP = {
  "be776ded-7b7c-4a83-94db-ae1810e59253": "TB01",
  "d737b884-b5f5-4605-934a-f4ea7936c622": "TB02",
  "3cc5c290-2762-451b-8ce0-fb9d506c31d6": "TB",
  "5de31d7c-4be8-46d9-9dac-635c7a8b98a9": "MC",
  "98e9626f-1784-4275-af24-d1d10e352a24": "YDL",
  "1d4462ed-1650-49bd-94ba-dc36ad4904c9": "WF"
};

export default function ChatView({
  conversation,
  messages = [],
  onSendMessage,
  templates = [],
  onStatusChange,
  onContactAdded,
  onRefreshMessages,
}) {
  const [inputValue, setInputValue] = useState("");
  const [showTemplates, setShowTemplates] = useState(false);
  const [quickSuggestions, setQuickSuggestions] = useState([]);
  const [selectedSuggestion, setSelectedSuggestion] = useState(0);
  const [addContactOpen, setAddContactOpen] = useState(false);
  const [contactName, setContactName] = useState("");
  const [sessionTimeLeft, setSessionTimeLeft] = useState(null);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [isSessionActive, setIsSessionActive] = useState(false);
  const [replyToMessage, setReplyToMessage] = useState(null);
  const [ocrResult, setOcrResult] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = async () => {
    setRefreshing(true);
    await onRefreshMessages?.();
    setTimeout(() => setRefreshing(false), 1000);
  };
  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrCopied, setOcrCopied] = useState(false);

  const handleExtractText = async (imagePath) => {
    setOcrLoading(true);
    setOcrResult(null);
    try {
      const response = await axios.post(`${API}/messages/extract-text`, { image_path: imagePath });
      setOcrResult(response.data.text || "No text found in image");
    } catch (error) {
      setOcrResult("Failed to extract text. Please try again.");
    } finally {
      setOcrLoading(false);
    }
  };

  const copyOcrText = () => {
    navigator.clipboard.writeText(ocrResult);
    setOcrCopied(true);
    setTimeout(() => setOcrCopied(false), 2000);
  };

  const isAdmin = (() => { try { return JSON.parse(localStorage.getItem("user"))?.role === "admin"; } catch { return false; } })();
  const deleteMessage = async (messageId) => {
    if (!window.confirm("Delete this message?")) return;
    try {
      await axios.delete(`${API}/messages/${messageId}`);
      setMessages(prev => prev.filter(m => m.id !== messageId));
      toast.success("Message deleted");
    } catch (error) {
      toast.error("Failed to delete message");
    }
  };
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Calculate and update session time remaining
  useEffect(() => {
    const calculateSessionTime = () => {
      if (!conversation?.last_incoming_message_time) {
        setIsSessionActive(false);
        setSessionTimeLeft(null);
        return;
      }

      const lastIncoming = new Date(conversation.last_incoming_message_time);
      const now = new Date();
      const expiresAt = new Date(lastIncoming.getTime() + 24 * 60 * 60 * 1000);
      const remaining = expiresAt - now;

      if (remaining <= 0) {
        setIsSessionActive(false);
        setSessionTimeLeft(null);
      } else {
        setIsSessionActive(true);
        const hours = Math.floor(remaining / (1000 * 60 * 60));
        const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
        setSessionTimeLeft({ hours, minutes, total: remaining });
      }
    };

    calculateSessionTime();
    const interval = setInterval(calculateSessionTime, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [conversation?.last_incoming_message_time]);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setInputValue(val);
    // Show quick suggestions when typing /
    if (val.startsWith('/')) {
      const search = val.slice(1).toLowerCase();
      const filtered = templates.filter(t => 
        t.name?.toLowerCase().includes(search) || 
        t.content?.toLowerCase().includes(search)
      );
      setQuickSuggestions(filtered.slice(0, 5));
      setSelectedSuggestion(0);
    } else {
      setQuickSuggestions([]);
    }
  };

  const handleSend = () => {
    if (!inputValue.trim()) return;
    onSendMessage(inputValue, "text", replyToMessage);
    setInputValue("");
    setQuickSuggestions([]);
    setReplyToMessage(null);
    inputRef.current?.focus();
  };

  const handleAddContact = async () => {
    try {
      await axios.post(`${API}/contacts/add-from-conversation`, null, {
        params: {
          phone_number: conversation.phone_number,
          name: contactName.trim() || null
        }
      });
      toast.success("Contact saved");
      setAddContactOpen(false);
      setContactName("");
      if (onContactAdded) onContactAdded();
    } catch (error) {
      console.error("Error adding contact:", error);
      toast.error("Failed to add contact");
    }
  };

  const handleKeyPress = (e) => {
    if (quickSuggestions.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedSuggestion(prev => Math.min(prev + 1, quickSuggestions.length - 1));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedSuggestion(prev => Math.max(prev - 1, 0));
        return;
      }
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        const selected = quickSuggestions[selectedSuggestion];
        if (selected) {
          setInputValue(selected.content);
          setQuickSuggestions([]);
          inputRef.current?.focus();
        }
        return;
      }
      if (e.key === "Escape") {
        setQuickSuggestions([]);
        return;
      }
    }
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleTemplateSelect = (template) => {
    setInputValue(template.content);
    setShowTemplates(false);
    inputRef.current?.focus();
  };

  const getInitials = (name, phone) => {
    if (name) {
      return name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
    }
    return phone?.slice(-2) || "?";
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "read":
        return <CheckCheck className="h-3.5 w-3.5 text-blue-500" />;
      case "delivered":
        return <CheckCheck className="h-3.5 w-3.5 text-muted-foreground" />;
      case "sent":
        return <Check className="h-3.5 w-3.5 text-muted-foreground" />;
      case "failed":
        return <X className="h-3.5 w-3.5 text-destructive" />;
      default:
        return <Clock className="h-3.5 w-3.5 text-muted-foreground" />;
    }
  };

  const formatMessageTime = (timestamp) => {
    try {
      return format(new Date(timestamp), "MMM d, h:mm a");
    } catch {
      return "";
    }
  };

  const groupMessagesByDate = (messages) => {
    const groups = {};
    messages.forEach((msg) => {
      const date = format(new Date(msg.timestamp), "yyyy-MM-dd");
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(msg);
    });
    return groups;
  };

  const formatDateHeader = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (format(date, "yyyy-MM-dd") === format(today, "yyyy-MM-dd")) {
      return "Today";
    } else if (format(date, "yyyy-MM-dd") === format(yesterday, "yyyy-MM-dd")) {
      return "Yesterday";
    }
    return format(date, "MMMM d, yyyy");
  };

  if (!conversation) {
    return (
      <div
        className="flex-1 flex flex-col items-center justify-center bg-muted/30"
        data-testid="chat-view-empty"
      >
        <div className="h-20 w-20 rounded-full bg-primary/10 flex items-center justify-center mb-4">
          <Phone className="h-10 w-10 text-primary" />
        </div>
        <h3 className="text-xl font-semibold mb-2">WhatsApp Support</h3>
        <p className="text-muted-foreground text-center max-w-md">
          Select a conversation from the list to view messages and respond to
          customers
        </p>
      </div>
    );
  }

  const messageGroups = groupMessagesByDate(messages);

  return (
    <div className="flex-1 flex flex-col bg-background" data-testid="chat-view">
      {/* Header */}
      <header className="glass-header px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Avatar className="h-10 w-10">
            <AvatarFallback className="bg-primary/10 text-primary font-medium">
              {getInitials(conversation.customer_name, conversation.phone_number)}
            </AvatarFallback>
          </Avatar>
          <div>
            <h3 className="font-semibold flex items-center gap-2">
              {conversation.customer_name || conversation.phone_number}
              {(conversation.account || WHATSAPP_ACCOUNT_MAP[conversation.whatsapp_config_id]) && (
                <span style={{background:"#dcfce7",color:"#166534",border:"1px solid #86efac",borderRadius:"4px",padding:"1px 6px",fontSize:"11px",fontWeight:"700"}}>
                  {conversation.account || WHATSAPP_ACCOUNT_MAP[conversation.whatsapp_config_id]}
                </span>
              )}
            </h3>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span className="font-mono select-text cursor-text">{conversation.phone_number}</span>
              {conversation.order_id && (
                <>
                  <span>•</span>
                  <Badge variant="outline" className="text-xs font-mono">
                    #{conversation.order_id}
                  </Badge>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* WhatsApp Session Indicator */}
          <div
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
              isSessionActive
                ? "bg-green-100 text-green-700 border border-green-200"
                : "bg-red-100 text-red-700 border border-red-200"
            }`}
            data-testid="session-indicator"
          >
            <Timer className="h-3.5 w-3.5" />
            {isSessionActive ? (
              <span>
                {sessionTimeLeft?.hours}h {sessionTimeLeft?.minutes}m left
              </span>
            ) : (
              <span>Session Expired</span>
            )}
          </div>

          {/* Add Contact Button */}
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setAddContactOpen(true)}
            data-testid="add-contact-btn"
          >
            <UserPlus className="h-4 w-4 mr-2" />
            Save Contact
          </Button>

          {/* AI Bot Tag - Show if AI handled this conversation */}
          {conversation.ai_handled && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-100 text-violet-700 rounded-full text-sm font-medium">
              <Bot className="h-4 w-4" />
              <span>AI Handled</span>
            </div>
          )}

          <Button
            variant="ghost"
            size="icon"
            onClick={handleRefresh}
            title="Refresh messages"
            disabled={refreshing}
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <MoreVertical className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem>View Customer Details</DropdownMenuItem>
              <DropdownMenuItem>View All Orders</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-destructive">
                Block Number
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Messages Area */}
      <ScrollArea className="flex-1 p-4">
        <div className="max-w-3xl mx-auto space-y-4">
          {Object.entries(messageGroups).map(([date, dateMessages]) => (
            <div key={date}>
              {/* Date Header */}
              <div className="flex justify-center my-4">
                <span className="chat-bubble-system">
                  {formatDateHeader(date)}
                </span>
              </div>

              {/* Messages */}
              {dateMessages.map((message) => {
                const isOutgoing = message.direction === "outgoing";

return (
                  <div
                    key={message.id}
                    id={`msg-${message.whatsapp_message_id || message.id}`}
                    className={`flex mb-2 animate-fade-in transition-all duration-500 group ${
                      isOutgoing ? "justify-end" : "justify-start"
                    }`}
                    data-testid={`message-${message.id}`}
                    onDoubleClick={() => !message.media_local_path && setReplyToMessage(message)}
                    style={{cursor: message.media_local_path ? 'default' : 'pointer'}}
                    title={message.media_local_path ? "" : "Double-click to reply"}
                  >
                    <div
                      className={`relative ${
                        isOutgoing ? "chat-bubble-user" : "chat-bubble-customer"
                      }`}
                    >
                      {/* Message Action Menu */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            className={`absolute top-1 ${isOutgoing ? 'left-1' : 'right-1'} bg-black/40 hover:bg-black/60 text-white p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity z-10`}
                          >
                            <ChevronDown className="h-3.5 w-3.5" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align={isOutgoing ? "start" : "end"} className="w-36">
                          <DropdownMenuItem onClick={() => setReplyToMessage(message)}>
                            <Reply className="h-4 w-4 mr-2" />
                            Reply
                          </DropdownMenuItem>
                          {message.media_local_path && (
                            <DropdownMenuItem onClick={() => {
                              const link = document.createElement('a');
                              link.href = `${process.env.REACT_APP_BACKEND_URL}/api/images/${message.media_local_path.split("/images/")[1]}`;
                              link.download = message.media_local_path.split("/").pop();
                              link.click();
                            }}>
                              <Download className="h-4 w-4 mr-2" />
                              Download
                            </DropdownMenuItem>
                          )}
                          {message.media_local_path && (
                            <DropdownMenuItem onClick={() => {
                              const imgPath = `/api/images/${message.media_local_path.split("/images/")[1]}`;
                              handleExtractText(imgPath);
                            }}>
                              <ScanText className="h-4 w-4 mr-2" />
                              Extract Text
                            </DropdownMenuItem>
                          )}
                          {isAdmin && (
                            <DropdownMenuItem 
                              onClick={() => deleteMessage(message.id)}
                              className="text-red-500"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                      {/* Quoted Reply Display */}
                      {message.context_message_id && (
                        <QuotedReply 
                          contextMessageId={message.context_message_id}
                          messages={messages}
                          isOutgoing={isOutgoing}
                        />
                      )}

                      {/* Media Preview */}
                      {message.media_local_path && (
                        <div className="mb-2">
                          <img
                            src={`${process.env.REACT_APP_BACKEND_URL}/api/images/${message.media_local_path.split("/images/")[1]}`}
                            alt="Media"
                            className="max-w-[200px] rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                            onClick={() => setLightboxImage(`${process.env.REACT_APP_BACKEND_URL}/api/images/${message.media_local_path.split("/images/")[1]}`)}
                          />
                        </div>
                      )}

                      {/* Message Content - Hide [Image] placeholder when image is shown */}
                      {message.content && !(message.media_local_path && (message.content === "[Image]" || message.content.startsWith("[Image]"))) && (
                        <p className="whitespace-pre-wrap break-words select-text cursor-text">
                          {message.content}
                        </p>
                      )}

                      {/* Reaction emoji */}
                      {message.reaction && (
                        <div className="absolute -bottom-3 right-1 bg-white rounded-full shadow px-1 text-base border border-gray-100">
                          {message.reaction}
                        </div>
                      )}
                      {/* Timestamp & Status */}
                      <div
                        className={`flex items-center gap-1 mt-1 text-xs ${
                          isOutgoing
                            ? "text-primary-foreground/70 justify-end"
                            : "text-muted-foreground"
                        }`}
                      >
                        <span>{formatMessageTime(message.timestamp)}</span>
                        {isOutgoing && getStatusIcon(message.status)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>

      {/* Reply Preview */}
      {/* OCR Result Popup */}
      {(ocrLoading || ocrResult) && (
        <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 z-50 bg-white rounded-xl shadow-2xl border p-4 w-96 max-h-80">
          <div className="flex justify-between items-center mb-2">
            <h3 className="font-semibold text-sm">Extracted Text</h3>
            <button onClick={() => setOcrResult(null)} className="text-gray-400 hover:text-gray-600">✕</button>
          </div>
          {ocrLoading ? (
            <div className="flex items-center gap-2 text-gray-500 py-4 justify-center">
              <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
              Extracting text...
            </div>
          ) : (
            <>
              <div className="bg-gray-50 rounded p-3 text-sm max-h-48 overflow-y-auto whitespace-pre-wrap select-text">
                {ocrResult}
              </div>
              <button
                onClick={copyOcrText}
                className="mt-2 w-full flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg py-2 text-sm"
              >
                {ocrCopied ? <><CheckCheck className="h-4 w-4" /> Copied!</> : <><ClipboardCopy className="h-4 w-4" /> Copy All</>}
              </button>
            </>
          )}
        </div>
      )}
      {replyToMessage && (
        <div className="border-t px-4 py-2 bg-muted/30 flex items-center gap-2">
          <div className="flex-1 border-l-4 border-primary pl-3 py-1">
            <p className="text-xs font-medium text-muted-foreground">
              Replying to {replyToMessage.direction === 'incoming' ? 'Customer' : 'yourself'}
            </p>
            <p className="text-sm truncate text-foreground">
              {replyToMessage.content || (replyToMessage.media_local_path ? '[Image]' : '[Message]')}
            </p>
          </div>
          <button 
            onClick={() => setReplyToMessage(null)}
            className="p-1.5 rounded-full hover:bg-muted transition-colors"
            title="Cancel reply"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
          </button>
        </div>
      )}

      {/* Input Area */}
      <div className="border-t p-4">
        {/* Session Expired Warning */}
        {!isSessionActive && conversation?.last_incoming_message_time && (
          <div className="max-w-3xl mx-auto mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-800">
                WhatsApp 24-hour session expired
              </p>
              <p className="text-xs text-amber-700 mt-0.5">
                You can only send pre-approved template messages. Use the Templates button to select one.
              </p>
            </div>
          </div>
        )}

        <div className="max-w-3xl mx-auto flex items-end gap-2">
          {/* Quick Templates */}
          <Popover open={showTemplates} onOpenChange={setShowTemplates}>
            <PopoverTrigger asChild>
              <Button
                variant={!isSessionActive ? "default" : "ghost"}
                size="icon"
                className={`shrink-0 ${!isSessionActive ? "animate-pulse" : ""}`}
                data-testid="templates-button"
              >
                <Zap className="h-5 w-5" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80" align="start">
              <div className="space-y-2">
                <h4 className="font-medium">
                  {!isSessionActive ? "Template Messages (Required)" : "Quick Replies"}
                </h4>
                {!isSessionActive && (
                  <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                    Session expired. Only template messages can be sent.
                  </p>
                )}
                {templates.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No templates available. Add templates in Settings.
                  </p>
                ) : (
                  <div className="space-y-1 max-h-[180px] overflow-y-auto pr-1">
                    {templates.map((template) => (
                      <div
                        key={template.id}
                        className="w-full p-2 border rounded-md cursor-pointer bg-white hover:border-primary transition-colors"
                        onClick={() => handleTemplateSelect(template)}
                      >
                        <div className="font-medium text-sm text-primary">{template.name}</div>
                        <div className="text-xs text-muted-foreground line-clamp-2 break-words">
                          {template.content}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </PopoverContent>
          </Popover>

          {/* Attachment Button */}
          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file && onSendMessage) {
                onSendMessage(file, "image");
              }
              e.target.value = "";
            }}
          />
          <Button 
            variant="ghost" 
            size="icon" 
            className="shrink-0"
            onClick={() => fileInputRef.current?.click()}
            disabled={!isSessionActive}
          >
            <Paperclip className="h-5 w-5" />
          </Button>

          {/* Message Input */}
          <div className="flex-1 relative">
            {/* Quick Reply Suggestions */}
            {quickSuggestions.length > 0 && (
              <div className="absolute bottom-12 left-0 right-0 bg-white border rounded-lg shadow-lg z-50 max-h-48 overflow-y-auto">
                {quickSuggestions.map((template, idx) => (
                  <div
                    key={template.id}
                    className={`px-3 py-2 cursor-pointer hover:bg-primary/10 ${idx === selectedSuggestion ? 'bg-primary/10' : ''}`}
                    onClick={() => {
                      setInputValue(template.content);
                      setQuickSuggestions([]);
                      inputRef.current?.focus();
                    }}
                  >
                    <div className="font-medium text-sm text-primary">/{template.name}</div>
                    <div className="text-xs text-muted-foreground truncate">{template.content}</div>
                  </div>
                ))}
              </div>
            )}
            <Input
              ref={inputRef}
              placeholder={isSessionActive ? "Type a message..." : "Session expired - use templates only"}
              value={inputValue}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              className="pr-12"
              disabled={!isSessionActive}
              data-testid="message-input"
            />
          </div>

          {/* Send Button */}
          <Button
            onClick={handleSend}
            disabled={!inputValue.trim() || !isSessionActive}
            className="shrink-0"
            data-testid="send-button"
          >
            <Send className="h-5 w-5" />
          </Button>
        </div>
      </div>

      {/* Add Contact Dialog */}
      <Dialog open={addContactOpen} onOpenChange={setAddContactOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save Contact</DialogTitle>
            <DialogDescription>
              Add this customer to your contacts list
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label>Phone Number</Label>
              <Input 
                value={conversation?.phone_number || ""} 
                disabled 
                className="mt-1 font-mono"
              />
            </div>
            <div>
              <Label>Contact Name (optional)</Label>
              <Input 
                placeholder="Enter contact name"
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddContactOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddContact}>
              Save Contact
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image Lightbox Modal */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
          onClick={() => setLightboxImage(null)}
        >
          <button
            className="absolute top-4 right-4 text-white text-3xl hover:text-gray-300 z-50"
            onClick={() => setLightboxImage(null)}
          >
            ✕
          </button>
          <img
            src={lightboxImage}
            alt="Full size"
            className="max-w-[90vw] max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
